import { useEffect } from "react";

import styles from "./settings.module.css";

export type ToastKind = "error" | "done";

/**
 * A message that cannot be scrolled past.
 *
 * **This replaces a banner at the top of the page, and the reason is worth
 * keeping.** The refusal that decision S1 exists to deliver — "this chair has 3
 * appointments still to come" — was rendered above a settings page long enough
 * to scroll, so the one message the whole rule was built to show arrived off
 * screen. A rule the user never reads is a rule that did not fire.
 *
 * `position: fixed`, so it sits over the page wherever the reader happens to
 * be. **Errors stay until dismissed**; a refusal is something to act on, and a
 * message that vanishes on a timer is one the reader may still be reaching for.
 * Confirmations clear themselves after a few seconds, because "saved" needs to
 * be seen and then got out of the way.
 *
 * `role="alert"` on errors so a screen reader is interrupted, `role="status"`
 * on confirmations so it is not.
 */
export function Toast({
  kind,
  message,
  onDismiss,
}: {
  kind: ToastKind;
  message: string;
  onDismiss: () => void;
}): React.JSX.Element {
  useEffect(() => {
    if (kind === "error") return;

    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
    // `message` is in the deps so a second confirmation restarts the clock
    // rather than inheriting the remainder of the first one's.
  }, [kind, message, onDismiss]);

  return (
    <div
      className={kind === "error" ? styles.toastError : styles.toastDone}
      role={kind === "error" ? "alert" : "status"}
      data-testid={kind === "error" ? "settings-error" : "settings-done"}
    >
      <span className={styles.toastIcon} aria-hidden="true">
        {kind === "error" ? "!" : "✓"}
      </span>
      <p className={styles.toastText}>{message}</p>
      <button
        type="button"
        className={styles.toastClose}
        onClick={onDismiss}
        aria-label="Dismiss this message"
        data-testid="toast-dismiss"
      >
        ✕
      </button>
    </div>
  );
}
